import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  FormlyFieldConfig,
  FormlyForm,
  FormlyFormOptions,
} from '@ngx-formly/core';
import { FormBuilderService } from '../../../../core/services/form-builder.service';
import { IFormBuilderFieldsData } from '../../../../core/models/form-builder-fields-data.model';
import { Subject, distinctUntilChanged, takeUntil } from 'rxjs';
import { FormBuilderManipulatorClass } from '../../../../core/utils/form-builder-manipulator-class.utils';
import {
  EIremboFormlyFieldTypes,
  FormStateService,
  IIrembogovBasicLabelKeyPair,
} from '@irembo-andela/irembogov3-common';
import { generateNewFieldKeyFromSettingsLabel } from '../../../../core/utils/form-fields-formly-configurations/form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import { default as deepEqual } from 'fast-deep-equal/es6';
import { EServiceChangeRequestToggleHeaders } from '../../../../core/models/service-change-request-toggle-header-section.enum';

@Component({
  selector: 'irembogov-preview-service-change-request-json-formdata-widget',
  templateUrl:
    './preview-service-change-request-json-formdata-widget.component.html',
  styleUrls: [
    './preview-service-change-request-json-formdata-widget.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PreviewServiceChangeRequestJsonFormdataWidgetComponent
  implements OnInit, OnDestroy
{
  previewDataFields!: FormlyFieldConfig[];

  subjectControl$: Subject<void> = new Subject<void>();

  reviewModeEnabled = false;

  form = new FormGroup({});
  model: Record<string, unknown> = {};
  options: FormlyFormOptions = { formState: { useMock: true } };
  formBuilderManipulator!: FormBuilderManipulatorClass;
  EServiceChangeRequestToggleHeaders = EServiceChangeRequestToggleHeaders;
  headerOptions: IIrembogovBasicLabelKeyPair<EServiceChangeRequestToggleHeaders>[] =
    [
      {
        label: 'Preview Form',
        key: EServiceChangeRequestToggleHeaders.PREVIEW,
      },
      {
        label: 'Form Model',
        key: EServiceChangeRequestToggleHeaders.FORM_MODEL,
      },
    ];
  selectedOption: EServiceChangeRequestToggleHeaders =
    EServiceChangeRequestToggleHeaders.PREVIEW;

  @Input() showHiddenAndHideFieldControls = false;
  @Input() showFormModel = false;
  @Input() viewOnlyMode = false;

  @ViewChild('formly') formly!: FormlyForm;

  constructor(
    private formBuilderService: FormBuilderService,
    private formStateService: FormStateService,
    private injector: Injector,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.formBuilderManipulator = new FormBuilderManipulatorClass(
      this.injector
    );
  }

  ngOnInit(): void {
    if (this.showHiddenAndHideFieldControls) {
      this.options.formState = {
        ...this.options.formState,
        showHiddenAndHideFieldControls: this.showHiddenAndHideFieldControls,
        getTranslation: this.formStateService.getTranslation.bind(
          this.formStateService
        ),
        translate: this.formStateService.translate,
      };
    }
    this.previewDataFields =
      this.formBuilderService.formlyFieldsSubject$.getValue().fields;
    this.reviewModeEnabled =
      this.formBuilderService.reviewModeEnabled$.getValue();

    this.formBuilderService.formlyFieldsSubject$
      .pipe(takeUntil(this.subjectControl$))
      .subscribe((formBuilderFieldsData: IFormBuilderFieldsData) => {
        this.previewDataFields = [...formBuilderFieldsData.fields];
        this.form.reset();
        this.options?.resetModel?.({});
        this.applyViewOnlyModeToForm();
        this.changeDetectorRef.detectChanges();
      });
    this.formBuilderService.reviewModeEnabled$
      .pipe(takeUntil(this.subjectControl$))
      .subscribe((isEnabled: boolean) => {
        this.reviewModeEnabled = isEnabled;
        this.changeDetectorRef.markForCheck();
      });

    this.formStateService.setFormlyFormState({
      ...this.options.formState,
      getTranslation: this.formStateService.getTranslation.bind(
        this.formStateService
      ),
      translate: this.formStateService.translate,
    });

    this.formStateService
      .getFormlyFormStateSubject()
      .pipe(
        takeUntil(this.subjectControl$),
        distinctUntilChanged((a: any, b: any) => {
          return deepEqual(this.options.formState, b);
        })
      )
      .subscribe({
        next: (formState: any) => {
          this.options.formState = {
            ...formState,
            getTranslation: this.formStateService.getTranslation.bind(
              this.formStateService
            ),
            translate: this.formStateService.translate,
          };
          this.changeDetectorRef.markForCheck();
          this.changeDetectorRef.detectChanges();
        },
      });
  }

  addNewSectionToFields() {
    const label = `New Section`;

    const newSection: FormlyFieldConfig = {
      props: { label },
      fieldGroup: [],
      type: EIremboFormlyFieldTypes['formly-group'],
    };

    const sectionField = generateNewFieldKeyFromSettingsLabel(
      newSection,
      this.formBuilderService
    );
    this.formBuilderManipulator.addNewSectionToFields(sectionField);
  }

  onTriggerValidations() {
    if (this.viewOnlyMode) return;
    console.log('Model', this.model);
  }

  private applyViewOnlyModeToForm(): void {
    if (this.viewOnlyMode) {
      this.form.disable({ emitEvent: false });
    } else if (this.form.disabled) {
      this.form.enable({ emitEvent: false });
    }
  }

  onHeaderOptionChange(option: EServiceChangeRequestToggleHeaders) {
    this.selectedOption = option;
  }

  getTransformedModel() {
    const transformedModel: any = {};

    const transformFields = (
      fields: FormlyFieldConfig[],
      model: any = {},
      output: any = {}
    ) => {
      fields.forEach(field => {
        const fieldKey = field.key as string;

        const isRequired = field.props?.required || false;
        const isHidden = field.props?.['hide'] || false;
        const isHiddenField = field.props?.['hideField'] || false;

        const requiredSymbol = '<sup>*</sup>';
        const hideSymbol = '<sup>-</sup>';
        const hideFieldSymbol = '<sup>+</sup>';

        let suffix = '';
        if (isRequired) suffix += requiredSymbol;
        if (isHidden) suffix += hideSymbol;
        if (isHiddenField) suffix += hideFieldSymbol;

        if (field.fieldGroup && field.fieldGroup.length > 0) {
          const subModel = fieldKey ? model?.[fieldKey] : model;
          const subOutput = fieldKey
            ? (output[suffix ? `${fieldKey}${suffix}` : fieldKey] = {})
            : output;

          transformFields(field.fieldGroup, subModel, subOutput);
        } else if (fieldKey != null) {
          const keyWithAsterisk = suffix ? `${fieldKey}${suffix}` : fieldKey;
          output[keyWithAsterisk] = model?.[fieldKey];
        }
      });
    };

    this.previewDataFields.forEach(field => {
      transformFields([field], this.model, transformedModel);
    });

    return transformedModel;
  }

  ngOnDestroy(): void {
    this.subjectControl$.next();
    this.subjectControl$.complete();
  }
}
